import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'

export const Route = createFileRoute('/(onboarding)/(documents)/terms-of-use')({
    component: TermsOfUse,
})

function TermsOfUse() {
    return (
        <>
            <Helmet>
                <title>Terms of Use - What The F*** Are You Wearing</title>
                <meta
                    name="description"
                    content="Terms of Use for What The F*** Are You Wearing Platform"
                />
            </Helmet>

            <div className="glass-background min-h-dvh pt-safe">
                <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mb-12 text-center">
                        <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
                            Terms of Use
                        </h1>
                        <p className="mt-4 text-xl text-gray-600">
                            Last updated 28 January 2026
                        </p>
                        <div className="mx-auto mt-4 h-1 w-24 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" />
                    </div>

                    {/* Introduction */}
                    <div className="mb-8 rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                        <div className="space-y-3 leading-relaxed text-gray-700">
                            <p>
                                Welcome to{' '}
                                <strong>What The F*** Are You Wearing</strong>{' '}
                                ("<strong>WTFAYW</strong>", "
                                <strong>the Platform</strong>", "
                                <strong>we</strong>", "<strong>us</strong>").
                            </p>
                            <p>
                                We're operated by{' '}
                                <strong>Softwear Labs Pty Ltd</strong> (ACN
                                691948633), based in Sydney, NSW, Australia.
                            </p>
                            <p className="rounded border-l-4 border-blue-500 bg-blue-50 p-4 text-sm">
                                <strong>
                                    By using our website or app, you agree to
                                    these Terms. If you don't, please don't use
                                    the Platform.
                                </strong>
                            </p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="space-y-8">
                        {/* 1. Who Can Use WTFAYW */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">
                                    1
                                </span>
                                Who Can Use WTFAYW
                            </h2>
                            <ul className="space-y-3">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        You must be{' '}
                                        <strong>13 years or older</strong> (or
                                        the minimum legal age in your country).
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        If you're under 18, you must have{' '}
                                        <strong>
                                            parental or guardian permission
                                        </strong>
                                        .
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        You need to{' '}
                                        <strong>create an account</strong> to
                                        post or curate collections.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        You're responsible for keeping your{' '}
                                        <strong>login details safe</strong>.
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 2. What You Can Do Here */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-bold text-purple-600">
                                    2
                                </span>
                                What You Can Do Here
                            </h2>
                            <p className="mb-4 text-gray-700">
                                WTFAYW lets you:
                            </p>
                            <ul className="mb-4 space-y-2 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        ✓
                                    </span>
                                    <span>
                                        Save and share products and looks from
                                        around the web
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        ✓
                                    </span>
                                    <span>
                                        Curate collections and moodboards
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        ✓
                                    </span>
                                    <span>
                                        Use AI "Try-On" features if available
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        ✓
                                    </span>
                                    <span>
                                        Subscribe to paid tiers (Free / Pro /
                                        Max)
                                    </span>
                                </li>
                            </ul>
                            <p className="rounded border-l-4 border-purple-500 bg-purple-50 p-4 text-sm text-purple-900">
                                <strong>Use the Platform responsibly</strong> —
                                don't upload or link to anything illegal,
                                hateful, or infringing.
                            </p>
                        </section>

                        {/* 3. Your Content */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-sm font-bold text-green-600">
                                    3
                                </span>
                                Your Content
                            </h2>
                            <ul className="space-y-3">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        <strong>You own</strong> the content you
                                        upload (photos, looks, descriptions,
                                        etc.).
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        By posting it, you give us a{' '}
                                        <strong>
                                            worldwide, royalty-free licence
                                        </strong>{' '}
                                        to host, display, and share it on
                                        WTFAYW.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        You promise you have{' '}
                                        <strong>the right to post it</strong>{' '}
                                        (i.e., it's yours or you have
                                        permission).
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        •
                                    </span>
                                    <span className="text-gray-700">
                                        We may{' '}
                                        <strong>remove or hide content</strong>{' '}
                                        that breaks these Terms or is reported
                                        as inappropriate.
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 4. AI "Try-On" Feature */}
                        <section className="rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50 to-blue-50 p-6 shadow-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-100 text-sm font-bold text-cyan-600">
                                    4
                                </span>
                                AI "Try-On" Feature
                            </h2>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-cyan-600">
                                        →
                                    </span>
                                    <span>
                                        AI Try-Ons are for{' '}
                                        <strong>
                                            creative and styling use only
                                        </strong>{' '}
                                        — not guaranteed to be accurate or
                                        realistic.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-cyan-600">
                                        →
                                    </span>
                                    <span>
                                        Images generated through Try-On are{' '}
                                        <strong>
                                            automatically stored in your account
                                            archive
                                        </strong>
                                        .
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-cyan-600">
                                        →
                                    </span>
                                    <span>
                                        Once generated,{' '}
                                        <strong>
                                            no refunds are given for Try-On
                                            credits
                                        </strong>
                                        .
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-cyan-600">
                                        →
                                    </span>
                                    <span>
                                        You agree not to upload{' '}
                                        <strong>
                                            explicit, harmful, or non-consensual
                                            images
                                        </strong>
                                        .
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 5. Subscriptions & Payments */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-600">
                                    5
                                </span>
                                Subscriptions & Payments
                            </h2>
                            <div className="mb-4 space-y-2 text-gray-700">
                                <div className="flex gap-3">
                                    <span className="font-semibold">Free:</span>
                                    <span>5 Try-Ons / month</span>
                                </div>
                                <div className="flex gap-3">
                                    <span className="font-semibold">Pro:</span>
                                    <span>100 Try-Ons / month</span>
                                </div>
                                <div className="flex gap-3">
                                    <span className="font-semibold">Max:</span>
                                    <span>500 Try-Ons / month</span>
                                </div>
                            </div>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-indigo-500">
                                        •
                                    </span>
                                    <span>
                                        All prices are in <strong>AUD</strong>{' '}
                                        and processed through our secure payment
                                        partner (e.g. Stripe).
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-indigo-500">
                                        •
                                    </span>
                                    <span>
                                        Subscriptions{' '}
                                        <strong>renew automatically</strong>{' '}
                                        each month unless cancelled through your
                                        account.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-indigo-500">
                                        •
                                    </span>
                                    <span>
                                        We don't issue refunds for partial
                                        periods or unused Try-Ons.
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 6. Intellectual Property */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-100 text-sm font-bold text-teal-600">
                                    6
                                </span>
                                Intellectual Property
                            </h2>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-teal-500">
                                        •
                                    </span>
                                    <span>
                                        The WTFAYW name, logo, brand design,
                                        software, and platform content are{' '}
                                        <strong>
                                            owned by Softwear Labs Pty Ltd
                                        </strong>
                                        .
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-teal-500">
                                        •
                                    </span>
                                    <span>
                                        You may not{' '}
                                        <strong>copy, modify, or reuse</strong>{' '}
                                        any part of the Platform without our
                                        written permission.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-teal-500">
                                        •
                                    </span>
                                    <span>
                                        When you save external products,{' '}
                                        <strong>
                                            trademarks and images belong to
                                            their respective owners
                                        </strong>
                                        .
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 7. Community Standards & Content Policy */}
                        <section className="rounded-2xl border border-red-100 bg-gradient-to-br from-red-50 to-orange-50 p-6 shadow-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-sm font-bold text-red-600">
                                    7
                                </span>
                                Community Standards & Content Policy
                            </h2>
                            <div className="mb-4 rounded-lg border-2 border-red-300 bg-red-100 p-4">
                                <p className="font-bold text-red-800">
                                    ZERO TOLERANCE POLICY
                                </p>
                                <p className="mt-2 text-red-700">
                                    We have <strong>zero tolerance</strong> for
                                    objectionable content or abusive users. Any
                                    user who violates our community standards
                                    will have their content removed and their
                                    account permanently banned from the
                                    Platform.
                                </p>
                            </div>
                            <p className="mb-3 text-gray-700">
                                The following content and behaviours are
                                strictly prohibited:
                            </p>
                            <ul className="mb-4 space-y-2">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-red-500">✗</span>
                                    <span className="text-gray-700">
                                        <strong>
                                            Explicit, violent, or illegal content
                                        </strong>{' '}
                                        including hate speech, pornography, or
                                        content promoting violence
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-red-500">✗</span>
                                    <span className="text-gray-700">
                                        <strong>Harassment or bullying</strong>{' '}
                                        of other users in any form
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-red-500">✗</span>
                                    <span className="text-gray-700">
                                        <strong>Spam or misleading content</strong>{' '}
                                        including fake reviews or impersonation
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-red-500">✗</span>
                                    <span className="text-gray-700">
                                        <strong>
                                            Copyright or trademark infringement
                                        </strong>{' '}
                                        posting content you don't have rights to
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-red-500">✗</span>
                                    <span className="text-gray-700">
                                        Attempting to{' '}
                                        <strong>hack, scrape, or misuse</strong>{' '}
                                        the Platform
                                    </span>
                                </li>
                            </ul>
                            <div className="space-y-3 rounded-lg bg-white/50 p-4">
                                <p className="font-semibold text-gray-800">
                                    Reporting & Enforcement
                                </p>
                                <ul className="space-y-2 text-sm text-gray-700">
                                    <li className="flex gap-2">
                                        <span className="text-red-500">→</span>
                                        <span>
                                            Users can <strong>report</strong>{' '}
                                            objectionable content or abusive
                                            users directly through the app
                                        </span>
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-red-500">→</span>
                                        <span>
                                            Users can <strong>block</strong>{' '}
                                            other users to immediately hide
                                            their content
                                        </span>
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-red-500">→</span>
                                        <span>
                                            We review all reports and take
                                            action{' '}
                                            <strong>within 24 hours</strong>
                                        </span>
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-red-500">→</span>
                                        <span>
                                            Violations result in{' '}
                                            <strong>content removal</strong> and{' '}
                                            <strong>account termination</strong>
                                        </span>
                                    </li>
                                </ul>
                            </div>
                            <p className="mt-4 text-sm text-gray-600 italic">
                                We may also change or discontinue parts of the
                                Platform at any time.
                            </p>
                        </section>

                        {/* 8. Limitation of Liability */}
                        <section className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 to-orange-50 p-6 shadow-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-700">
                                    8
                                </span>
                                Limitation of Liability
                            </h2>
                            <div className="space-y-3 text-gray-700">
                                <p>
                                    We provide WTFAYW <strong>"as is"</strong>.
                                </p>
                                <p>
                                    To the extent allowed by law, we're not
                                    responsible for:
                                </p>
                                <ul className="space-y-2">
                                    <li className="flex gap-3">
                                        <span className="mt-1 text-amber-600">
                                            •
                                        </span>
                                        <span>
                                            Losses from using or relying on
                                            Try-On results
                                        </span>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="mt-1 text-amber-600">
                                            •
                                        </span>
                                        <span>
                                            Issues with linked third-party
                                            products or stores
                                        </span>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="mt-1 text-amber-600">
                                            •
                                        </span>
                                        <span>
                                            Interruptions, bugs, or data loss
                                        </span>
                                    </li>
                                </ul>
                                <p className="rounded border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900">
                                    If we are found liable for anything, our
                                    total liability is limited to{' '}
                                    <strong>
                                        the amount you've paid to us in the past
                                        12 months
                                    </strong>
                                    .
                                </p>
                            </div>
                        </section>

                        {/* 9. Termination */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-100 text-sm font-bold text-pink-600">
                                    9
                                </span>
                                Termination
                            </h2>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-pink-500">
                                        •
                                    </span>
                                    <span>
                                        You can{' '}
                                        <strong>delete your account</strong>{' '}
                                        anytime through Settings or by emailing
                                        us.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-pink-500">
                                        •
                                    </span>
                                    <span>
                                        We may <strong>end your access</strong>{' '}
                                        if you seriously breach these Terms.
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 10. Updates to These Terms */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-600">
                                    10
                                </span>
                                Updates to These Terms
                            </h2>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-violet-500">
                                        •
                                    </span>
                                    <span>
                                        We may{' '}
                                        <strong>
                                            update these Terms from time to time
                                        </strong>
                                        .
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-violet-500">
                                        •
                                    </span>
                                    <span>
                                        We'll post any new version here and may
                                        alert you in-app.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-violet-500">
                                        •
                                    </span>
                                    <span>
                                        If you keep using WTFAYW after changes,
                                        that means you accept them.
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 11. Governing Law */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">
                                    11
                                </span>
                                Governing Law
                            </h2>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        •
                                    </span>
                                    <span>
                                        These Terms are governed by the{' '}
                                        <strong>
                                            laws of New South Wales, Australia
                                        </strong>
                                        .
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        •
                                    </span>
                                    <span>
                                        Any disputes will be handled in{' '}
                                        <strong>NSW courts</strong> or through
                                        arbitration if agreed.
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 12. Contact Us */}
                        <section className="rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 p-6 text-white shadow-lg sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-bold text-gray-900">
                                    12
                                </span>
                                Contact Us
                            </h2>
                            <div className="space-y-2 rounded-xl bg-white/10 p-6 backdrop-blur-sm">
                                <p className="text-lg font-bold">
                                    Softwear Labs Pty Ltd
                                </p>
                                <p className="text-gray-200">
                                    Suite 2, Level 3 7-15 Macquarie Place
                                    <br />
                                    Sydney 2000 NSW, Australia
                                </p>
                                <p className="pt-2">
                                    <span className="text-gray-300">
                                        Email:{' '}
                                    </span>
                                    <a
                                        href="mailto:support@wtf.style"
                                        className="font-medium text-blue-300 underline hover:text-blue-200"
                                    >
                                        support@wtf.style
                                    </a>
                                </p>
                            </div>
                        </section>

                        {/* Footer */}
                        <div className="border-t border-gray-200 pt-8 pb-4">
                            <p className="rounded-xl bg-gray-50 p-6 text-center text-sm text-gray-600">
                                By using What The F*** Are You Wearing, you
                                agree to these Terms of Use.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
